/* eslint-disable */
import React, { useEffect, useState, useMemo, useRef } from "react";
import Select from "react-select";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import apiService from "services/apiService";
import months from "util/months";
import * as XLSX from "xlsx";
import DataDisplay from "./DataDisplay";
import { formatMixedTimestampDisplayDate } from "./dateUtils";
// NOTE: shared with DataDisplay so the workbook contains exactly the WFH/WFO
// values shown on screen (see workLocationUtils for what used to differ).
import {
  getOrphanWorkLocationDates,
  getWorkLocationHours,
  getWorkLocationHoursForDateKey,
  resolveUserWorkLocations,
} from "./workLocationUtils";
import { sortByKey } from "util/sortArrByName";
import ApproveTimesheet from "pages/viewTimesheet/ApproveTimesheet";

import { useTranslation } from "react-i18next";

import GenericModal from "components/Modal";
import HeaderButtons from "components/Button/HeaderButtons";

const schema = yup.object().shape({
  email: yup.string().notRequired().nullable(),
  project: yup.string().notRequired().nullable(),
  year: yup.number().required("Year is Required"),
  month: yup.string().required("Month is Required"),
});

interface OptionProps {
  value: string;
  label: string;
  id?: string;
}

const YearOption = [
  {
    label: new Date().getFullYear(),
    value: new Date().getFullYear(),
  },
  {
    label: new Date().getFullYear() - 1,
    value: new Date().getFullYear() - 1,
  },
];

export default function ExportTimeSheet() {
  const methods = useForm({ resolver: yupResolver(schema) });

  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<OptionProps[]>([
    { value: "", label: "All" },
  ]);

  const [project, setProject] = useState<OptionProps[]>([
    { value: "", label: "All" },
  ]);
  const [timesheet, setTimesheet] = useState<any>();
  const [enable, setEnable] = useState(true);
  const [payload1, setPayload1] = useState<any>(null);
  const [message, setMessage] = useState(false);
  const [userActive, setUserActive] = useState(`users`);
  const [month, setMonth] = useState<string>();
  const [year, setYear] = useState<string>();
  const [approveModal, setApproveModal] = useState(false);
  const [selectedUserForApproval, setSelectedUserForApproval] = useState<any>(null);
  const [workLocations, setWorkLocations] = useState<Record<string, any>>({});
  const [workLocationSummary, setWorkLocationSummary] = useState<Record<string, {
    wfhHours: number | string;
    wfoHours: number | string;
  }> | null>(null);

  // --- Caches for globally resolving names regardless of active pagination page ---
  const projectMap = useRef<Record<string, string>>({});
  const userMap = useRef<Record<string, string>>({});
  const allProjectsFetched = useRef(false);

  // --- Pagination and Scroll States for User Dropdown ---
  const [selectedUserOption, setSelectedUserOption] = useState<OptionProps | null>({ value: "", label: "All" });
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;

  //--- pagination and scroll states for projects dropdown
  const [selectedProjectOption, setSelectedProjectOption] =
    useState<OptionProps | null>({ value: "", label: "All" });

  const [projectPage, setProjectPage] = useState(0);
  const [projectSearch, setProjectSearch] = useState("");
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const currentUserEmail = currentUser?.email;
  const PROJECT_PAGE_SIZE = 10;

  // Sync react-select indicator adjustments
  const employeeSelectStyles = useMemo(
    () => ({
      control: (base: any) => ({
        ...base,
        backgroundColor: 'var(--select-bg)',
        borderColor: 'var(--select-border)',
      }),
      menu: (base: any) => ({
        ...base,
        backgroundColor: 'var(--select-menu-bg)',
        border: '1px solid var(--select-border)',
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
          ? 'rgb(79, 70, 229)'
          : state.isFocused
            ? 'var(--select-option-hover)'
            : 'var(--select-menu-bg)',
        color: state.isSelected ? 'white' : 'var(--select-text)',
      }),
      singleValue: (base: any) => ({
        ...base,
        color: 'var(--select-text)',
      }),
      input: (base: any) => ({
        ...base,
        color: 'var(--select-text)',
      }),
      placeholder: (base: any) => ({
        ...base,
        color: 'var(--select-placeholder)',
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        minWidth: 64,
        width: 64,
        justifyContent: "flex-end",
      }),
      loadingIndicator: (base: any) => ({
        ...base,
        marginRight: 8,
        padding: 0,
      }),
      indicatorSeparator: (base: any) => ({
        ...base,
        marginLeft: 2,
        marginRight: 2,
      }),
    }),
    []
  );

  const getProjectsFromPayload = (payload: any) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  };

  const getUsersFromPayload = (payload: any) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  };

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/${userActive}?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const options = users.map((user: any) => {
        if (user.email) {
          userMap.current[user.email] = user.displayName;
        }
        return {
          id: user.id,
          value: user.email,
          label: user.displayName,
        };
      });

      setData((prev) => {
        const baseArray = append ? prev : [{ value: "", label: "All" }];
        const merged = [...baseArray, ...options];
        const dedup = new Map<string, OptionProps>();

        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });

        // Separate "All", sort the rest, and combine
        const dedupValues = Array.from(dedup.values());
        const allOption = dedupValues.find(item => item.value === "");
        const otherOptions = sortByKey(dedupValues.filter(item => item.value !== ""), "label");

        return allOption ? [allOption, ...otherOptions] : otherOptions;
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch, userActive, refresh]);

  const fetchProjects = async (
    page: number,
    searchValue: string,
    append: boolean
  ) => {
    if (loadingProjects) return;

    setLoadingProjects(true);

    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(PROJECT_PAGE_SIZE));

      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/project?${query.toString()}`);

      const payload = res.data;
      const projects = getProjectsFromPayload(payload);

      const options = projects.map((item: any) => {
        projectMap.current[String(item.projectId)] = item.name;
        return {
          value: item.projectId,
          label: item.name,
        };
      });

      setProject((prev) => {
        const base = append ? prev : [{ value: "", label: "All" }];
        const merged = [...base, ...options];
        const dedup = new Map<string, OptionProps>();

        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });

        // Separate "All", sort the rest, and combine
        const dedupValues = Array.from(dedup.values());
        const allOption = dedupValues.find(item => item.value === "");
        const otherOptions = sortByKey(dedupValues.filter(item => item.value !== ""), "label");

        return allOption ? [allOption, ...otherOptions] : otherOptions;
      });

      setProjectPage(page);

      if (typeof payload?.last === "boolean") {
        setHasMoreProjects(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreProjects(page < payload.totalPages - 1);
      } else {
        setHasMoreProjects(options.length === PROJECT_PAGE_SIZE);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchProjects(0, projectSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [projectSearch]);

  const checkAndFetchMissingProjects = async (tsData: any[]) => {
    if (allProjectsFetched.current) return;

    let hasMissing = false;
    for (const userItem of tsData) {
      const projectsArray = Array.isArray(userItem?.data) ? userItem.data : [];
      for (const proj of projectsArray) {
        if (proj?.projectId && !projectMap.current[String(proj.projectId)]) {
          hasMissing = true;
          break;
        }
      }
      if (hasMissing) break;
    }

    if (hasMissing) {
      try {
        const query = new URLSearchParams();
        query.set("page", "0");
        query.set("size", "5000");

        const res = await apiService.get(`api/project?${query.toString()}`);
        const allProjects = getProjectsFromPayload(res.data);

        allProjects.forEach((item: any) => {
          projectMap.current[String(item.projectId)] = item.name;
        });

        allProjectsFetched.current = true;
      } catch (err) {
        console.log("Error fetching missing projects", err);
      }
    }
  };

  // Helper to safely parse Work Location map structure and aggregate values
  const parseWorkLocationResponse = (resData: any) => {
    if (!resData) return { summary: { wfhHours: "-", wfoHours: "-" }, dailyData: {} };

    const dailyData = resData.dailyBreakdown || resData;
    let totalWfh = 0;
    let totalWfo = 0;
    let hasCalc = false;
    const parsedMap: Record<string, any> = {};

    Object.keys(dailyData).forEach(key => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        parsedMap[key] = dailyData[key];
        const day = dailyData[key];
        if (day) {
          if (day.wfhHours != null && !isNaN(Number(day.wfhHours))) {
            totalWfh += Number(day.wfhHours);
            hasCalc = true;
          }
          if (day.wfoHours != null && !isNaN(Number(day.wfoHours))) {
            totalWfo += Number(day.wfoHours);
            hasCalc = true;
          }
        }
      }
    });

    const summary = {
      wfhHours: resData.wfhHours != null ? resData.wfhHours : (hasCalc ? totalWfh : "-"),
      wfoHours: resData.wfoHours != null ? resData.wfoHours : (hasCalc ? totalWfo : "-"),
    };

    return { summary, dailyData: parsedMap };
  };

  const getWorkLocationById = (
    selectedEmail: string,
    selectedYear?: number,
    selectedMonth?: string
  ) => {
    if (!selectedEmail) {
      setWorkLocations({});
      setWorkLocationSummary(null);
      return;
    }

    const selectedUser = data.find((item) => item.value === selectedEmail);

    if (!selectedUser || !selectedUser.id) {
      setWorkLocations({});
      setWorkLocationSummary(null);
      return;
    }

    let fromDate = "";
    if (selectedYear && selectedMonth) {
      const monthIndex = new Date(`${selectedMonth} 1, ${selectedYear}`).getMonth();
      if (!Number.isNaN(monthIndex)) {
        fromDate = `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}-01`;
      }
    }

    const query = fromDate ? `?fromDate=${fromDate}` : "";

    apiService
      .get(`api/work-location/summary/${selectedUser.id}${query}`)
      .then((res) => {
        const { summary, dailyData } = parseWorkLocationResponse(res.data);

        setWorkLocationSummary({ [selectedUser.value]: summary });
        setWorkLocations({ [selectedUser.value]: dailyData, [selectedUser.id as string]: dailyData });
      })
      .catch((err) => {
        console.log("work location error", err);
        setWorkLocations({});
        setWorkLocationSummary(null);
      });
  };

  const getUserNameByEmail = (email: string): string => {
    return userMap.current[email] || "";
  };

  const getProjectNameById = (projectId: string): string => {
    return projectMap.current[String(projectId)] || "";
  };

  const getUserNameForExport = (item: any): string => {
    return getUserNameByEmail(item?.email) || item?.userName || item?.email || "User";
  };

  const getProjectNameForExport = (detail: any): string => {
    return getProjectNameById(detail?.projectId) || detail?.projectName || `Project-${detail?.projectId ?? "NA"}`;
  };

  const sanitizeSheetName = (name: string) =>
    String(name || "Sheet")
      .replace(/[\\/?*\[\]:]/g, "_")
      .trim() || "Sheet";

  const getUniqueSheetName = (
    workbook: XLSX.WorkBook,
    baseName: string,
    sheetCounterRef: { value: number }
  ) => {
    const safeBase = sanitizeSheetName(baseName);
    let candidate = safeBase.slice(0, 31);

    while (workbook.SheetNames.includes(candidate)) {
      const suffix = `_${sheetCounterRef.value++}`;
      const maxBaseLength = Math.max(1, 31 - suffix.length);
      candidate = `${safeBase.slice(0, maxBaseLength)}${suffix}`;
    }

    return candidate;
  };

  let monthStore: string = "";
  let yearStore: string = "";

  function exportTimeSheetToExcel(
    data: any[],
    userName: string,
    projectName: string,
    workLocations: Record<string, any> = {}
  ) {
    const wb = XLSX.utils.book_new();
    const sheetCounterRef = { value: 1 };

    data.forEach((item) => {
      setMonth("");
      setYear("");

      monthStore = item.month;
      yearStore = item.year;

      const user_name = getUserNameForExport(item);
      const userWorkLocations = resolveUserWorkLocations(workLocations, item);
      const orphanKeys = getOrphanWorkLocationDates(item, userWorkLocations);
      let orphanRowsAdded = false;
      let userSheetCreated = false;

      (Array.isArray(item?.data) ? item.data : []).forEach((detail: any) => {
        const project_name = getProjectNameForExport(detail);

        if (Array.isArray(detail?.detail) && detail.detail.length > 0) {
          const wsName = getUniqueSheetName(
            wb,
            `${user_name}_${project_name}`,
            sheetCounterRef
          );

          const sortedDetails = [...detail.detail].sort((a, b) => a.day - b.day);

          const wsData = sortedDetails.map((task: any) => {
            const locationHours = getWorkLocationHours(
              userWorkLocations,
              task.day,
              task.date
            );

            const formattedTaskDate =
              formatMixedTimestampDisplayDate(task?.date) ||
              `${task.day}-${item.month}-${item.year}`;

            return {
              Date: formattedTaskDate,
              Task: task.task,
              WFH: locationHours.wfh,
              WFO: locationHours.wfo,
              Hours: task.hour,
              Comment: task.comment,
            };
          });

          if (!orphanRowsAdded && orphanKeys.length > 0) {
            const orphanRows = orphanKeys.map((dateKey) => {
              const orphanHours = getWorkLocationHoursForDateKey(
                userWorkLocations,
                dateKey
              );
              const day = dateKey.split("-")[2];

              return {
                Date: `${day}-${item.month}-${item.year}`,
                Task: "-",
                WFH: orphanHours.wfh,
                WFO: orphanHours.wfo,
                Hours: "-",
                Comment: "-",
              };
            });

            wsData.push(...orphanRows);
            orphanRowsAdded = true;
          }

          const totalHours = sortedDetails.reduce(
            (sum: number, task: any) => sum + (task.hour || 0),
            0
          );

          wsData.push({
            Date: "",
            Task: "Total Hours",
            WFH: "",
            WFO: "",
            Hours: totalHours,
            Comment: "",
          });

          const ws = XLSX.utils.json_to_sheet(wsData);

          ["A", "B", "C", "D", "E", "F"].forEach((col) => {
            const headerAddress = `${col}1`;
            if (!ws[headerAddress]) ws[headerAddress] = { v: "" };

            ws[headerAddress].s = {
              font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "medium", color: { rgb: "000000" } },
                bottom: { style: "medium", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
              },
            };
          });

          const totalRowIndex = wsData.length;

          ["A", "B", "C", "D", "E", "F"].forEach((col) => {
            const cellAddress = `${col}${totalRowIndex}`;
            if (!ws[cellAddress]) ws[cellAddress] = { v: "" };

            ws[cellAddress].s = {
              font: { bold: true, sz: 13 },
              fill: { fgColor: { rgb: "FFD966" } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "thick", color: { rgb: "000000" } },
                bottom: { style: "thick", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
              },
            };
          });

          for (let R = 2; R < totalRowIndex; R++) {
            ["A", "B", "C", "D", "E", "F"].forEach((col) => {
              const cellAddress = `${col}${R}`;
              if (!ws[cellAddress]) ws[cellAddress] = { v: "" };

              ws[cellAddress].s = {
                border: {
                  top: { style: "thin", color: { rgb: "D3D3D3" } },
                  bottom: { style: "thin", color: { rgb: "D3D3D3" } },
                  left: { style: "thin", color: { rgb: "D3D3D3" } },
                  right: { style: "thin", color: { rgb: "D3D3D3" } },
                },
              };
            });
          }

          ws["!cols"] = [
            { wch: 15 },
            { wch: 35 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 45 },
          ];

          ws["!rows"] = [{ hpt: 25 }];
          XLSX.utils.book_append_sheet(wb, ws, wsName);
          userSheetCreated = true;
        }
      });

      if (!userSheetCreated && orphanKeys.length > 0) {
        const orphanWsData = orphanKeys.map((dateKey) => {
          const orphanHours = getWorkLocationHoursForDateKey(userWorkLocations, dateKey);
          const day = dateKey.split("-")[2];
          return {
            Date: `${day}-${item.month}-${item.year}`,
            Task: "-",
            WFH: orphanHours.wfh,
            WFO: orphanHours.wfo,
            Hours: "-",
            Comment: "-",
          };
        });

        const wlSheetName = getUniqueSheetName(
          wb,
          `${user_name}_WorkLocation`,
          sheetCounterRef
        );

        const wlWs = XLSX.utils.json_to_sheet(orphanWsData);

        ["A", "B", "C", "D", "E", "F"].forEach((col) => {
          const addr = `${col}1`;
          if (!wlWs[addr]) wlWs[addr] = { v: "" };
          wlWs[addr].s = {
            font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "ED7D31" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "medium", color: { rgb: "000000" } },
              bottom: { style: "medium", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        });

        wlWs["!cols"] = [
          { wch: 15 },
          { wch: 35 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 45 },
        ];
        wlWs["!rows"] = [{ hpt: 25 }];

        XLSX.utils.book_append_sheet(wb, wlWs, wlSheetName);
      } else if (!userSheetCreated) {
        const fallbackWsData = [
          {
            Date: "-",
            Task: "-",
            WFH: "-",
            WFO: "-",
            Hours: item?.totalHour ?? "-",
            Comment: item?.comment || "-",
          },
          {
            Date: "",
            Task: "Total Hours",
            WFH: "",
            WFO: "",
            Hours: item?.totalHour ?? 0,
            Comment: "",
          },
        ];

        const fallbackSheetName = getUniqueSheetName(
          wb,
          `${user_name}_Summary`,
          sheetCounterRef
        );

        const fallbackWs = XLSX.utils.json_to_sheet(fallbackWsData);

        ["A", "B", "C", "D", "E", "F"].forEach((col) => {
          const headerAddress = `${col}1`;
          if (!fallbackWs[headerAddress]) fallbackWs[headerAddress] = { v: "" };

          fallbackWs[headerAddress].s = {
            font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4472C4" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "medium", color: { rgb: "000000" } },
              bottom: { style: "medium", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        });

        fallbackWs["!cols"] = [
          { wch: 15 },
          { wch: 35 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 45 },
        ];
        fallbackWs["!rows"] = [{ hpt: 25 }];

        XLSX.utils.book_append_sheet(wb, fallbackWs, fallbackSheetName);
      }
    });

    if (wb.SheetNames.length > 0) {
      XLSX.writeFile(wb, `TimeSheet_${monthStore}-${yearStore}.xlsx`);
    }
  }

  function buildQueryString(payload: any): string {
    const params = Object.keys(payload)
      .filter(
        (key) =>
          payload[key] !== undefined &&
          payload[key] !== "" &&
          payload[key] !== "All"
      )
      .map((key) => {
        const encodedKey = encodeURIComponent(key);
        let value = payload[key];

        if (key === "projectId") {
          value = Number(value);
        }
        return `${encodedKey}=${value}`;
      })
      .join("&");

    return `api/timesheet?${params}`;
  }

  useEffect(() => {
    methods.setValue("email", data[0].value);
    methods.setValue("year", YearOption[0].value);
    methods.setValue("month", months[new Date().getMonth()].value);
  }, []);

  useEffect(() => {
    if (project.length > 0) {
      methods.setValue("project", "");
      setSelectedProjectOption(project[0]);
    }
  }, [project]);

  const onSubmit: SubmitHandler<any> = async (payload: any) => {
    const fullPayload = { ...payload, userActive };
    setPayload1(fullPayload);

    const email = payload.email || "";
    const year = payload.year;
    const month = payload.month;
    const selectedUser = data.find((item) => item.value === email);

    const tsPayload = { ...fullPayload };
    tsPayload.projectId = tsPayload.project;
    delete tsPayload.project;
    const queryString = buildQueryString(tsPayload);

    try {
      const res = await apiService.get(queryString);
      let timesheetData = res.data || [];

      await checkAndFetchMissingProjects(timesheetData);

      const emailsToFetch = new Set<string>();
      timesheetData.forEach((item: any) => {
        if (item.email) emailsToFetch.add(item.email);
      });

      if (selectedUser && selectedUser.value) {
        emailsToFetch.add(selectedUser.value);
      }

      const uniqueEmails = Array.from(emailsToFetch);
      const emailToIdMap = new Map<string, string | number>();
      const validKeys = new Set<string>();

      try {
        // Fetch users corresponding to the CURRENT tab
        const userRes = await apiService.get(`api/${userActive}?page=0&size=5000`);
        const allUsers = getUsersFromPayload(userRes.data);

        allUsers.forEach((u: any) => {
          if (u.email) {
            const lowerEmail = String(u.email).trim().toLowerCase();
            if (u.id) {
              emailToIdMap.set(u.email, u.id);
              emailToIdMap.set(lowerEmail, u.id);
            }
            validKeys.add(lowerEmail);
          }
          if (u.id != null) validKeys.add(String(u.id));
        });
      } catch (err) {
        console.log("Error fetching user mapping", err);
      }

      // STRICT TAB FILTER: Intersect timesheet data with the valid users for this tab
      // Ensure we only show timesheets for people who exist in the selected userActive state
      if (!email && Array.isArray(timesheetData)) {
        timesheetData = timesheetData.filter((item: any) => {
          const em = item.email ? String(item.email).trim().toLowerCase() : "";
          const id = item.id != null ? String(item.id) : "";
          const uId = item.userId != null ? String(item.userId) : "";

          return (em && validKeys.has(em)) || (id && validKeys.has(id)) || (uId && validKeys.has(uId));
        });
      }

      let fromDate = "";
      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      if (!Number.isNaN(monthIndex)) {
        fromDate = `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
      }
      const query = fromDate ? `?fromDate=${fromDate}` : "";

      const newWorkLocations: Record<string, any> = {};
      const newWorkLocationSummary: Record<string, any> = {};
      let wlHasDataForSelectedUser = false;

      if (uniqueEmails.length > 0) {
        const wlPromises = uniqueEmails.map((emailKey) => {
          const mappedId = emailToIdMap.get(emailKey) || emailToIdMap.get(String(emailKey).trim().toLowerCase());
          if (!mappedId) return Promise.resolve(null);

          return apiService.get(`api/work-location/summary/${mappedId}${query}`)
            .then((r) => ({ email: emailKey, mappedId, data: r.data }))
            .catch(() => null);
        });

        const wlResponses = await Promise.all(wlPromises);

        wlResponses.forEach((wlRes) => {
          if (wlRes && wlRes.data) {
            const { summary, dailyData } = parseWorkLocationResponse(wlRes.data);
            const tsItem = timesheetData.find((t: any) => t.email === wlRes.email);

            newWorkLocationSummary[wlRes.email] = summary;
            newWorkLocations[wlRes.email] = dailyData;

            newWorkLocationSummary[wlRes.mappedId] = summary;
            newWorkLocations[wlRes.mappedId] = dailyData;

            if (tsItem && tsItem.id) {
              newWorkLocationSummary[tsItem.id] = summary;
              newWorkLocations[tsItem.id] = dailyData;
            }

            if (selectedUser && wlRes.email === selectedUser.value) {
              wlHasDataForSelectedUser = Object.keys(dailyData).length > 0;
            }
          }
        });
      }

      setWorkLocationSummary(newWorkLocationSummary);
      setWorkLocations(newWorkLocations);

      if (!timesheetData.length && wlHasDataForSelectedUser && selectedUser && selectedUser.value !== "") {
        timesheetData = [
          {
            id: selectedUser.id || Date.now(),
            userName: selectedUser.label,
            email: selectedUser.value,
            year: Number(year),
            month: month,
            status: "No Timesheet",
            projectIds: "",
            submitDate: "",
            totalHour: 0,
            comment: "",
            data: [],
          },
        ];
      }

      setTimesheet(timesheetData);

      if (!timesheetData.length) {
        setMessage(true);
        setEnable(true);
      } else {
        setMessage(false);
        setEnable(false);
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    if (payload1 && refresh > 0) {
      const fetchRefreshData = async () => {
        const fullPayload = { ...payload1, userActive };
        const selectedUser = data.find((item) => item.value === fullPayload.email);
        const queryString = buildQueryString(fullPayload);

        try {
          const res = await apiService.get(queryString);
          let timesheetData = res.data || [];

          await checkAndFetchMissingProjects(timesheetData);

          const emailsToFetch = new Set<string>();
          timesheetData.forEach((item: any) => {
            if (item.email) emailsToFetch.add(item.email);
          });
          if (selectedUser && selectedUser.value) {
            emailsToFetch.add(selectedUser.value);
          }

          const uniqueEmails = Array.from(emailsToFetch);
          const emailToIdMap = new Map<string, string | number>();
          const validKeys = new Set<string>();

          try {
            // Fetch users corresponding to the CURRENT tab
            const userRes = await apiService.get(`api/${userActive}?page=0&size=5000`);
            const allUsers = getUsersFromPayload(userRes.data);

            allUsers.forEach((u: any) => {
              if (u.email) {
                const lowerEmail = String(u.email).trim().toLowerCase();
                if (u.id) {
                  emailToIdMap.set(u.email, u.id);
                  emailToIdMap.set(lowerEmail, u.id);
                }
                validKeys.add(lowerEmail);
              }
              if (u.id != null) validKeys.add(String(u.id));
            });
          } catch (err) {
            console.log("Error fetching user mapping", err);
          }

          // STRICT TAB FILTER: Intersect timesheet data with the valid users for this tab
          // Ensure we only show timesheets for people who exist in the selected userActive state
          if (!fullPayload.email && Array.isArray(timesheetData)) {
            timesheetData = timesheetData.filter((item: any) => {
              const em = item.email ? String(item.email).trim().toLowerCase() : "";
              const id = item.id != null ? String(item.id) : "";
              const uId = item.userId != null ? String(item.userId) : "";

              return (em && validKeys.has(em)) || (id && validKeys.has(id)) || (uId && validKeys.has(uId));
            });
          }

          let fromDate = "";
          const monthIndex = new Date(`${fullPayload.month} 1, ${fullPayload.year}`).getMonth();
          if (!Number.isNaN(monthIndex)) {
            fromDate = `${fullPayload.year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
          }
          const query = fromDate ? `?fromDate=${fromDate}` : "";

          const newWorkLocations: Record<string, any> = {};
          const newWorkLocationSummary: Record<string, any> = {};
          let wlHasDataForSelectedUser = false;

          if (uniqueEmails.length > 0) {
            const wlPromises = uniqueEmails.map((emailKey) => {
              const mappedId = emailToIdMap.get(emailKey) || emailToIdMap.get(String(emailKey).trim().toLowerCase());
              if (!mappedId) return Promise.resolve(null);

              return apiService.get(`api/work-location/summary/${mappedId}${query}`)
                .then((r) => ({ email: emailKey, mappedId, data: r.data }))
                .catch(() => null);
            });

            const wlResponses = await Promise.all(wlPromises);

            wlResponses.forEach((wlRes) => {
              if (wlRes && wlRes.data) {
                const { summary, dailyData } = parseWorkLocationResponse(wlRes.data);
                const tsItem = timesheetData.find((t: any) => t.email === wlRes.email);

                newWorkLocationSummary[wlRes.email] = summary;
                newWorkLocations[wlRes.email] = dailyData;

                newWorkLocationSummary[wlRes.mappedId] = summary;
                newWorkLocations[wlRes.mappedId] = dailyData;

                if (tsItem && tsItem.id) {
                  newWorkLocationSummary[tsItem.id] = summary;
                  newWorkLocations[tsItem.id] = dailyData;
                }

                if (selectedUser && wlRes.email === selectedUser.value) {
                  wlHasDataForSelectedUser = Object.keys(dailyData).length > 0;
                }
              }
            });
          }

          setWorkLocationSummary(newWorkLocationSummary);
          setWorkLocations(newWorkLocations);

          if (!timesheetData.length && wlHasDataForSelectedUser && selectedUser && selectedUser.value !== "") {
            timesheetData = [
              {
                id: selectedUser.id || Date.now(),
                userName: selectedUser.label,
                email: selectedUser.value,
                year: Number(fullPayload.year),
                month: fullPayload.month,
                status: "No Timesheet",
                projectIds: "",
                submitDate: "",
                totalHour: 0,
                comment: "",
                data: [],
              },
            ];
          }

          setTimesheet(timesheetData);

          if (!timesheetData.length) {
            setMessage(true);
            setEnable(true);
          } else {
            setMessage(false);
            setEnable(false);
          }
        } catch (err) {
          console.log("err", err);
        }
      };

      fetchRefreshData();
    }
  }, [refresh]);

  const onExport = () => {
    if (timesheet && payload1) {
      const email = payload1.email || "";
      const projectId = payload1.projectId || "";

      let projectName = "All";
      if (projectId !== "") {
        projectName = projectMap.current[String(projectId)] || "All";
      }

      let userName = "All";
      if (email !== "") {
        userName = userMap.current[email] || "All";
      }

      exportTimeSheetToExcel(timesheet, userName, projectName, workLocations);
    }
  };

  const { t } = useTranslation();

  const handlClose = () => {
    setMessage(false);
  };

  return (
    <div className="pl-2 text-slate-900 min-h-[calc(100vh-150px)] dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      <style>{`
        :root {
          --select-bg: white;
          --select-border: #d1d5db;
          --select-menu-bg: white;
          --select-text: #111827;
          --select-placeholder: #9ca3af;
          --select-option-hover: #f3f4f6;
        }
        .dark {
          --select-bg: #374151;
          --select-border: #4b5563;
          --select-menu-bg: #1f2937;
          --select-text: #f9fafb;
          --select-placeholder: #6b7280;
          --select-option-hover: #374151;
        }
      `}</style>

      <div className="flex gap-4 pb-4 mt-2">
        <HeaderButtons
          type="button"
          variant={userActive === "users" ? "selected" : "inactive"}
          onClick={() => {
            if (!loadingUsers && userActive !== "users") {
              setUserActive("users");
              setUserSearch("");
              setSelectedUserOption({ value: "", label: "All" });
              methods.setValue("email", "");
              if (payload1) {
                setPayload1((prev: any) => (prev ? { ...prev, email: "", userActive: "users" } : null));
              }
              setRefresh((prev) => prev + 1);
            }
          }}
          disabled={userActive === "inactive-users" && loadingUsers}
        >
          Active Users
        </HeaderButtons>

        <HeaderButtons
          type="button"
          variant={userActive === "inactive-users" ? "selected" : "inactive"}
          onClick={() => {
            if (!loadingUsers && userActive !== "inactive-users") {
              setUserActive("inactive-users");
              setUserSearch("");
              setSelectedUserOption({ value: "", label: "All" });
              methods.setValue("email", "");
              if (payload1) {
                setPayload1((prev: any) => (prev ? { ...prev, email: "", userActive: "inactive-users" } : null));
              }
              setRefresh((prev) => prev + 1);
            }
          }}
          disabled={userActive === "users" && loadingUsers}
        >
          Inactive Users
        </HeaderButtons>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex items-end gap-4 pb-0 flex-wrap">
            <div className="mb-4 w-56">
              <label className="block mb-2 pl-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("User")}
              </label>
              <Select
                {...methods.register("email", { required: true })}
                options={data}
                placeholder="Select Employee"
                className="w-full text-slate-900 dark:text-slate-100"
                styles={employeeSelectStyles}
                isLoading={loadingUsers}
                isClearable={selectedUserOption?.value !== ""}
                onMenuScrollToBottom={() => {
                  if (hasMoreUsers && !loadingUsers) {
                    void fetchUsers(userPage + 1, userSearch, true);
                  }
                }}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    setUserSearch(value);
                  }
                  return value;
                }}
                onChange={(selectedOption: OptionProps | null) => {
                  if (selectedOption) {
                    methods.setValue("email", selectedOption.value);
                    setSelectedUserOption(selectedOption);
                  } else {
                    methods.setValue("email", "");
                    setSelectedUserOption({ value: "", label: "All" });
                  }
                }}
                value={selectedUserOption}
              />
              {methods.formState.errors.email && (
                <span className="text-red-500 text-sm mt-1">
                  {methods.formState.errors.email.message}
                </span>
              )}
            </div>

            <div className="mb-4 w-56">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("Projects")}
              </label>
              <Select
                {...methods.register("project", { required: true })}
                isLoading={loadingProjects}
                isClearable={selectedProjectOption?.value !== ""}
                onMenuScrollToBottom={() => {
                  if (hasMoreProjects && !loadingProjects) {
                    void fetchProjects(projectPage + 1, projectSearch, true);
                  }
                }}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    setProjectSearch(value);
                  }
                  return value;
                }}
                options={project}
                placeholder="Select Project"
                className="w-full"
                onChange={(selectedOption: OptionProps | null) => {
                  if (selectedOption) {
                    methods.setValue("project", selectedOption.value);
                    setSelectedProjectOption(selectedOption);
                  } else {
                    methods.setValue("project", "");
                    setSelectedProjectOption({ value: "", label: "All" });
                  }
                }}
                styles={{
                  control: (base) => ({ ...base, backgroundColor: 'var(--select-bg)', borderColor: 'var(--select-border)' }),
                  menu: (base) => ({ ...base, backgroundColor: 'var(--select-menu-bg)', border: '1px solid var(--select-border)' }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? 'rgb(79, 70, 229)' : state.isFocused ? 'var(--select-option-hover)' : 'var(--select-menu-bg)',
                    color: state.isSelected ? 'white' : 'var(--select-text)',
                  }),
                  singleValue: (base) => ({ ...base, color: 'var(--select-text)' }),
                  input: (base) => ({ ...base, color: 'var(--select-text)' }),
                  placeholder: (base) => ({ ...base, color: 'var(--select-placeholder)' }),
                }}
              />
              {methods.formState.errors.project && (
                <span className="text-red-500 text-sm mt-1">
                  {methods.formState.errors.project.message}
                </span>
              )}
            </div>

            <div className="mb-4 w-56">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("Year")}
              </label>
              <Select
                {...methods.register("year", { required: true })}
                options={YearOption}
                placeholder="Select Year"
                className="w-full"
                onChange={(selectedOption) => {
                  selectedOption && methods.setValue("year", selectedOption.value);
                }}
                defaultValue={YearOption[0]}
                styles={{
                  control: (base) => ({ ...base, backgroundColor: 'var(--select-bg)', borderColor: 'var(--select-border)' }),
                  menu: (base) => ({ ...base, backgroundColor: 'var(--select-menu-bg)', border: '1px solid var(--select-border)' }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? 'rgb(79, 70, 229)' : state.isFocused ? 'var(--select-option-hover)' : 'var(--select-menu-bg)',
                    color: state.isSelected ? 'white' : 'var(--select-text)',
                  }),
                  singleValue: (base) => ({ ...base, color: 'var(--select-text)' }),
                  input: (base) => ({ ...base, color: 'var(--select-text)' }),
                  placeholder: (base) => ({ ...base, color: 'var(--select-placeholder)' }),
                }}
              />
              {methods.formState.errors.year && (
                <span className="text-red-500 text-sm mt-1">
                  {methods.formState.errors.year.message}
                </span>
              )}
            </div>

            <div className="mb-4 w-56">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {t("Month")}
              </label>
              <Select
                {...methods.register("month", { required: true })}
                options={months}
                placeholder="Select month"
                className="w-full"
                onChange={(selectedOption) => {
                  selectedOption && methods.setValue("month", selectedOption.value);
                }}
                defaultValue={months[new Date().getMonth()]}
                styles={{
                  control: (base) => ({ ...base, backgroundColor: 'var(--select-bg)', borderColor: 'var(--select-border)' }),
                  menu: (base) => ({ ...base, backgroundColor: 'var(--select-menu-bg)', border: '1px solid var(--select-border)' }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? 'rgb(79, 70, 229)' : state.isFocused ? 'var(--select-option-hover)' : 'var(--select-menu-bg)',
                    color: state.isSelected ? 'white' : 'var(--select-text)',
                  }),
                  singleValue: (base) => ({ ...base, color: 'var(--select-text)' }),
                  input: (base) => ({ ...base, color: 'var(--select-text)' }),
                  placeholder: (base) => ({ ...base, color: 'var(--select-placeholder)' }),
                }}
              />
              {methods.formState.errors.month && (
                <span className="text-red-500 text-sm mt-1">
                  {methods.formState.errors.month.message}
                </span>
              )}
            </div>

            <div className="flex gap-3 mb-4">
              <button
                className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-6 py-2.5 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                type="submit"
              >
                {t("View")}
              </button>

              <button
                className={`text-white font-medium rounded-lg text-sm px-6 py-2.5 ${enable ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800'}`}
                disabled={enable}
                onClick={onExport}
                type="button"
              >
                {t("Export")}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>

      {timesheet && (
        <DataDisplay
          currentUserId={currentUserEmail}
          data={timesheet}
          userName={getUserNameByEmail}
          projectName={getProjectNameById}
          workLocations={workLocations}
          workLocationSummary={workLocationSummary}
          onApprove={(user: any) => {
            setSelectedUserForApproval(user);
            setApproveModal(true);
          }}
        />
      )}

      {approveModal && selectedUserForApproval && (
        <GenericModal
          isOpen={approveModal}
          onClose={() => {
            setApproveModal(false);
            setSelectedUserForApproval(null);
          }}
          header="Approve Timesheet"
          customWidth="800px"
        >
          <ApproveTimesheet
            projects={selectedUserForApproval.projects}
            setApprove={(value: boolean) => {
              setApproveModal(value);
              if (!value) {
                setSelectedUserForApproval(null);
                setRefresh((prev) => prev + 1);
              }
            }}
            selectedUser={selectedUserForApproval.email}
            currentYear={selectedUserForApproval.year}
            currentMonth={selectedUserForApproval.month}
          />
        </GenericModal>
      )}

      {message && (
        <GenericModal
          isOpen={message}
          onClose={() => setMessage(false)}
          header="User Time sheet data"
        >
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="p-4 md:p-5 text-center">
              <p className="text-gray-500 dark:text-gray-300 text-xl mb-6 font-medium">
                Data is not exist{" "}
              </p>
              <svg
                className="mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-200"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              <button
                type="button"
                className="py-2.5 px-5 bg-red-400 hover:bg-red-700 ms-3 text-sm font-medium text-gray-900 focus:outline-none rounded-lg border border-gray-200"
                onClick={handlClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </GenericModal>
      )}
    </div>
  );
}